// SSE stream parsing shared across AI providers, and the paragraph-boundary split used while streaming.

// One parsed SSE line: text to emit and/or a signal that the stream has completed.
export type SSEParseResult = { text?: string; done?: boolean };

// Shared SSE reader. Handles the decode/buffer/line-split/done/error boilerplate common to every
// provider; each provider supplies a parseLine that knows its own wire format.
export const readSSEStream = async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    parseLine: (line: string) => SSEParseResult,
    onChunk: (text: string) => void,
    onDone: () => void,
    onError: (err: any) => void
) => {
    const decoder = new TextDecoder();
    let buffer = '';
    let doneCalled = false;
    const finish = () => { if (!doneCalled) { onDone(); doneCalled = true; } };
    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            for (const line of lines) {
                const { text, done: streamDone } = parseLine(line);
                if (text) onChunk(text);
                if (streamDone) { finish(); return; }
            }
        }
        finish();
    } catch (err) {
        onError(err);
        finish();
    }
};

// OpenAI / OpenRouter / Azure chat-completions SSE line.
export const parseChatCompletionLine = (line: string): SSEParseResult => {
    const trimmed = line.trim();
    if (!trimmed || !trimmed.startsWith('data:')) return {};
    const data = trimmed.replace(/^data:\s*/, '');
    if (data === '[DONE]') return { done: true };
    try {
        const parsed = JSON.parse(data);
        return { text: parsed.choices?.[0]?.delta?.content || parsed.choices?.[0]?.message?.content || '' };
    } catch { return {}; }
};

// OpenAI Responses API SSE line (emits { type: "response.output_text.delta", delta: "..." }).
export const parseResponsesLine = (line: string): SSEParseResult => {
    const trimmed = line.trim();
    if (!trimmed || !trimmed.startsWith('data:')) return {};
    const data = trimmed.replace(/^data:\s*/, '');
    if (data === '[DONE]') return { done: true };
    try {
        const parsed = JSON.parse(data);
        const text =
            (parsed?.type === 'response.output_text.delta' && typeof parsed?.delta === 'string' ? parsed.delta : '') ||
            (typeof parsed?.delta === 'string' ? parsed.delta : '');
        return { text };
    } catch { return {}; }
};

// Anthropic Messages API SSE line.
export const parseAnthropicLine = (line: string): SSEParseResult => {
    if (!line.startsWith('data: ')) return {};
    const jsonStr = line.slice(6).trim();
    if (!jsonStr) return {};
    try {
        const parsed = JSON.parse(jsonStr);
        if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') return { text: parsed.delta.text };
        if (parsed.type === 'message_stop') return { done: true };
        return {};
    } catch { return {}; }
};

// Splits accumulated streaming text at the last safe paragraph boundary (not inside an open code block).
// Completed paragraphs are rendered with memoised ReactMarkdown; the tail renders as plain text.
export function getStreamingSplit(text: string): { completed: string; tail: string } {
    const backtickCount = (text.match(/```/g) || []).length;
    const isInCodeBlock = backtickCount % 2 !== 0;
    const searchIn = isInCodeBlock ? text.slice(0, text.lastIndexOf('```')) : text;
    const lastBreak = searchIn.lastIndexOf('\n\n');
    if (lastBreak >= 0) {
        return { completed: text.slice(0, lastBreak + 2), tail: text.slice(lastBreak + 2) };
    }
    return { completed: '', tail: text };
}
